import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Paper, Box, Button, Typography, CircularProgress, Tabs, Tab, TabsActions } from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { createAdminTheme } from './adminTheme';
import {Link as RouterLink, useLocation, useNavigate} from "react-router-dom";
import { useToolbar } from "../../components/ToolbarContext";
import { testAnthropic } from './admin.service';
import { logError } from '../../shared/services/logger.service';
import { enqueueSnackbar } from 'notistack';
import TreeOperationsTab from './TreeOperationsTab';
import AccountsTab from './AccountsTab';
import BillingTab from './BillingTab';
import TimeOnSiteTab from './TimeOnSiteTab';
import AiPricingTab from './AiPricingTab';
import { usePermissions } from '../../shared/permissions/PermissionContext';

const AiOpsPage = lazy(() => import('./ai-ops/AiOpsPage'));
const LibraryPage = lazy(() => import('./library/LibraryPage'));

/** A deploy removes the previous build's chunks, so a lazy import can fail; without a boundary React unmounts the app. */
export class ChunkBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <Box sx={{ p: 3 }}>
        <Typography>This page could not be loaded. A newer version may have been deployed.</Typography>
        <Button variant="contained" sx={{ mt: 1 }} onClick={() => window.location.reload()}>Reload</Button>
      </Box>
    );
  }
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toolbarContext = useToolbar();
  const { me } = usePermissions();
  const outerTheme = useTheme();
  const adminTheme = useMemo(() => createAdminTheme(outerTheme), [outerTheme]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const path = location.pathname.replace(/\/$/, '');
  const aiOps = path === '/admin/ai-ops';
  const library = path === '/admin/library';
  const accountsPath = path === '/admin/accounts';
  const billingPath = path === '/admin/billing';
  const timeOnSitePath = path === '/admin/time-on-site';
  // Account and billing tools are for SavaQuan admins; the server refuses anyone else too.
  const accounts = accountsPath && me?.isAdmin === true;
  const billing = billingPath && me?.isAdmin === true;
  // People's whereabouts (V84): SavaQuan staff only, like billing.
  const timeOnSite = timeOnSitePath && me?.isAdmin === true;
  // SQ-712: which companies have AI pricing. Only SavaQuan turns it on.
  const aiPricingPath = path === '/admin/ai-pricing';
  const aiPricing = aiPricingPath && me?.isAdmin === true;
  const selected = aiOps ? 2 : library ? 3 : accounts ? 4 : billing ? 5 : timeOnSite ? 6 : aiPricing ? 7 : tabValue;
  // The admin-only tabs appear once `me` arrives, after MUI measured the bar:
  // without a re-measure the underline stays under the tab it was first placed at.
  const tabsActions = useRef<TabsActions>(null);
  useEffect(() => {
    tabsActions.current?.updateIndicator();
  }, [me?.isAdmin, selected]);

  useEffect(() => {
    toolbarContext.setTitle(aiOps ? 'AI Ops' : library ? 'Comparable library' : billing ? 'Billing' : timeOnSite ? 'Time on site'
      : aiPricing ? 'AI pricing' : 'Admin');
    toolbarContext.setCenteredContent(null);
  }, [toolbarContext, aiOps, library, billing, timeOnSite, aiPricing]);

  const handleTestAnthropicClick = async () => {
    setLoading(true);
    try {
      const response = await testAnthropic(navigate);
      enqueueSnackbar('Test Anthropic call successful!', { variant: 'success' });
      console.log('Test Anthropic response:', response);
    } catch (error) {
      logError('Error calling test-anthropic', {}, error as Error);
      enqueueSnackbar('Error calling test-anthropic endpoint', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    // The AI Ops, Library, Accounts, Billing, Time on site and AI pricing tabs are links; navigating here too would push a second history entry.
    if (newValue >= 2) return;
    setTabValue(newValue);
    if (aiOps || library || accountsPath || billingPath || timeOnSitePath || aiPricingPath) navigate('/admin');
  };

  // Company staff granted library curation reach the library without the platform tools.
  if (library && me && !me.isAdmin) {
    return (
      <ThemeProvider theme={adminTheme}>
        <Paper sx={{ height: '100%', overflow: 'auto', minWidth: 0 }}>
          <ChunkBoundary>
            <Suspense fallback={<CircularProgress sx={{ m: 3 }} />}>
              <LibraryPage />
            </Suspense>
          </ChunkBoundary>
        </Paper>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={adminTheme}>
    <Paper sx={{ height: '100%', overflow: 'hidden' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={selected} onChange={handleTabChange} action={tabsActions} aria-label="admin tabs" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label="Test Tools" {...a11yProps(0)} />
          <Tab label="Tree Operations" {...a11yProps(1)} />
          <Tab label="AI Ops" value={2} component={RouterLink} to="/admin/ai-ops" {...a11yProps(2)} />
          <Tab label="Library" value={3} component={RouterLink} to="/admin/library" {...a11yProps(3)} />
          {me?.isAdmin && <Tab label="Accounts" value={4} component={RouterLink} to="/admin/accounts" {...a11yProps(4)} />}
          {me?.isAdmin && <Tab label="Billing" value={5} component={RouterLink} to="/admin/billing" {...a11yProps(5)} />}
          {me?.isAdmin && <Tab label="Time on site" value={6} component={RouterLink} to="/admin/time-on-site" {...a11yProps(6)} />}
          {me?.isAdmin && <Tab label="AI pricing" value={7} component={RouterLink} to="/admin/ai-pricing" {...a11yProps(7)} />}
        </Tabs>
      </Box>

      <TabPanel value={selected} index={0}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="calc(100vh - 200px)" p={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Admin Test Tools
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Administrative functions and testing tools
          </Typography>
          
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Button 
              variant="contained" 
              size="large"
              onClick={handleTestAnthropicClick}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Testing...' : 'Test Anthropic'}
            </Button>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={selected} index={1}>
        <Box sx={{ height: 'calc(100vh - 120px)', overflow: 'auto' }}>
          <TreeOperationsTab />
        </Box>
      </TabPanel>

      <TabPanel value={selected} index={2}>
        <Box sx={{ height: 'calc(100vh - 120px)', overflow: 'auto', minWidth: 0 }}>
          <ChunkBoundary>
            <Suspense fallback={<CircularProgress sx={{ m: 3 }} />}>
              <AiOpsPage />
            </Suspense>
          </ChunkBoundary>
        </Box>
      </TabPanel>

      <TabPanel value={selected} index={3}>
        <Box sx={{ height: 'calc(100vh - 120px)', overflow: 'auto', minWidth: 0 }}>
          <ChunkBoundary>
            <Suspense fallback={<CircularProgress sx={{ m: 3 }} />}>
              <LibraryPage />
            </Suspense>
          </ChunkBoundary>
        </Box>
      </TabPanel>

      <TabPanel value={selected} index={4}>
        <Box sx={{ height: 'calc(100vh - 120px)', overflow: 'auto' }}>
          <AccountsTab />
        </Box>
      </TabPanel>

      <TabPanel value={selected} index={5}>
        <Box sx={{ height: 'calc(100vh - 120px)', overflow: 'auto' }}>
          <BillingTab />
        </Box>
      </TabPanel>

      <TabPanel value={selected} index={6}>
        <Box sx={{ height: 'calc(100vh - 120px)', overflow: 'auto' }}>
          <TimeOnSiteTab />
        </Box>
      </TabPanel>

      <TabPanel value={selected} index={7}>
        <Box sx={{ height: 'calc(100vh - 120px)', overflow: 'auto' }}>
          <AiPricingTab />
        </Box>
      </TabPanel>
    </Paper>
    </ThemeProvider>
  );
};

export default AdminPage;
